import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Printer, ShieldCheck, BadgeCheck } from "lucide-react";

export default function CoaPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${id}/coa`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setFailed(true));
  }, [id]);

  if (failed) {
    return <div className="flex min-h-screen items-center justify-center text-slate-400" data-testid="coa-not-found">Certificate not found.</div>;
  }
  if (!data) {
    return <div className="flex min-h-screen items-center justify-center font-mono text-xs tracking-[0.3em] text-slate-500" data-testid="coa-loading">VERIFYING CERTIFICATE...</div>;
  }

  const { coa } = data;
  const isLabReport = coa.provider === "janoshik" || coa.provider === "labdoc";
  const meta = [
    ["PRODUCT", `${data.product_name} (${data.product_code})`],
    ["FORMAT", data.dose],
    ["BATCH NUMBER", coa.batch_number],
    ["MANUFACTURED", coa.manufacture_date],
    ["TESTED", coa.test_date],
    ["EXPIRY", coa.expiry_date],
  ];
  const reportMeta = coa.provider === "labdoc"
    ? [
        ["SAMPLE", coa.sample],
        ["BATCH / REPORT", coa.batch_number],
        ["TESTING LABORATORY", coa.laboratory],
      ]
    : [
        ["CLIENT", coa.client],
        ["SAMPLE", coa.sample],
        ["MANUFACTURER", coa.manufacturer],
        ["BATCH", coa.batch_number],
      ];

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-32 sm:px-8" data-testid="coa-page">
      <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-4">
        <Link to={`/products/${data.product_id}`} data-testid="coa-back-button" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-slate-500 transition-colors duration-200 hover:text-[#E8E9EB]">
          <ArrowLeft className="h-3.5 w-3.5" /> BACK TO PRODUCT
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="coa-print-button"
          className="inline-flex items-center gap-2 rounded-full border border-[#6E7683]/60 px-5 py-2.5 font-mono text-[11px] tracking-[0.2em] text-[#E8E9EB] transition-colors duration-200 hover:bg-[#6E7683]/20"
        >
          <Printer className="h-3.5 w-3.5" /> PRINT / SAVE PDF
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="tracing print-light relative rounded-2xl bg-[#111114] p-8 sm:p-12"
        data-testid="coa-certificate"
      >
        {isLabReport ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-6 border-b border-white/10 pb-8">
              <div>
                <h1 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="coa-title">
                  Test Report
                </h1>
                <div className="mt-2 font-mono text-[10px] tracking-[0.3em] text-slate-500">{coa.laboratory.toUpperCase()}</div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.15em] text-slate-600">{coa.lab_email ? `${coa.lab_email} / ${coa.lab_web}` : coa.lab_web}</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500">TASK NUMBER</div>
                <div className="mt-1 font-mono text-xl text-white" data-testid="coa-task-number">#{coa.task_number}</div>
                <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-emerald-400">
                  <BadgeCheck className="h-3 w-3" /> VERIFIED BY LAB
                </div>
              </div>
            </div>

            <div className="grid gap-x-8 gap-y-5 border-b border-white/10 py-8 sm:grid-cols-2">
              {reportMeta.map(([k, v]) => (
                <div key={k}>
                  <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">{k}</div>
                  <div className="mt-1 font-mono text-sm text-white" data-testid={k === "SAMPLE" ? "coa-sample" : undefined}>{v}</div>
                </div>
              ))}
              <div>
                <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">TESTING ORDERED</div>
                <div className="mt-1 font-mono text-sm text-white">{coa.testing_ordered}</div>
              </div>
              <div>
                <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">SAMPLE RECEIVED</div>
                <div className="mt-1 font-mono text-sm text-white">{coa.sample_received}</div>
              </div>
            </div>

            <div className="border-b border-white/10 py-8">
              <div className="mb-3 font-mono text-[10px] tracking-[0.3em] text-[#E8E9EB]">TESTS REQUESTED</div>
              <p className="text-sm text-slate-300">{coa.tests_requested}</p>
            </div>

            <div className="py-8">
              <div className="mb-4 font-mono text-[10px] tracking-[0.3em] text-[#E8E9EB]">RESULTS</div>
              <div className="overflow-x-auto">
                <table className="w-full text-left" data-testid="coa-results-table">
                  <thead>
                    <tr className="border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-slate-500">
                      <th className="py-3 pr-4">SUBSTANCE</th>
                      <th className="py-3 text-right">CONTENT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coa.results.map((r) => (
                      <tr key={r.substance} className="border-b border-white/5 text-sm">
                        <td className="py-3.5 pr-4 text-white">{r.substance}</td>
                        <td className="py-3.5 text-right font-mono text-xs text-white">{r.content}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-6">
                <div className="mb-2 font-mono text-[10px] tracking-[0.3em] text-[#E8E9EB]">COMMENTS</div>
                <p className="text-sm text-slate-400">{coa.comments}</p>
              </div>
            </div>

            <div className="border-t border-white/10 pt-8">
              <div className="grid items-center gap-6 sm:grid-cols-2">
                <div>
                  <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">ANALYSIS CONDUCTED</div>
                  <div className="mt-1 font-mono text-sm text-white">{coa.analysis_conducted}</div>
                </div>
                <div className="sm:text-right">
                  <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">UNIQUE VERIFICATION KEY</div>
                  <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-[#E8E9EB]/30 bg-[#E8E9EB]/5 px-4 py-2">
                    <ShieldCheck className="h-4 w-4 text-[#E8E9EB]" />
                    <span className="font-mono text-xs tracking-[0.2em] text-[#E8E9EB]" data-testid="coa-verify-code">{coa.verify_code}</span>
                  </div>
                  <div className="mt-3">
                    <span className="font-mono text-[10px] tracking-[0.15em] text-slate-300" data-testid="coa-verify-link">
                      {coa.provider === "labdoc" ? `VERIFY AT ${coa.verify_url}` : "VERIFY AT https://janoshik.com"}
                    </span>
                  </div>
                </div>
              </div>
              <p className="mt-8 border-t border-white/5 pt-6 font-mono text-[10px] leading-relaxed tracking-[0.1em] text-slate-600">
                THIS TEST REPORT IS ISSUED BY {coa.laboratory.toUpperCase()} FOR THE LISTED SAMPLE AND BATCH.
                FOR LABORATORY RESEARCH USE ONLY. NOT FOR HUMAN CONSUMPTION.
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-start justify-between gap-6 border-b border-white/10 pb-8">
              <div>
                <div className="font-display text-xl font-extrabold tracking-[0.25em] text-white">
                  ALPHA<span className="text-[#E8E9EB]">PEPS</span>
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.3em] text-slate-500">QUALITY CONTROL DIVISION</div>
              </div>
              <div className="text-right">
                <h1 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl" data-testid="coa-title">
                  Certificate of Analysis
                </h1>
                <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 font-mono text-[10px] tracking-[0.2em] text-emerald-400">
                  <BadgeCheck className="h-3 w-3" /> AUTHENTICATED
                </div>
              </div>
            </div>

            <div className="grid gap-x-8 gap-y-5 border-b border-white/10 py-8 sm:grid-cols-2 lg:grid-cols-3">
              {meta.map(([k, v]) => (
                <div key={k}>
                  <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">{k}</div>
                  <div className="mt-1 font-mono text-sm text-white">{v}</div>
                </div>
              ))}
            </div>

            <div className="py-8">
              <div className="mb-4 font-mono text-[10px] tracking-[0.3em] text-[#E8E9EB]">ANALYTICAL RESULTS</div>
              <div className="overflow-x-auto">
                <table className="w-full text-left" data-testid="coa-results-table">
                  <thead>
                    <tr className="border-b border-white/10 font-mono text-[10px] tracking-[0.2em] text-slate-500">
                      <th className="py-3 pr-4">TEST</th>
                      <th className="py-3 pr-4">SPECIFICATION</th>
                      <th className="py-3 pr-4">RESULT</th>
                      <th className="py-3 text-right">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coa.tests.map((t) => (
                      <tr key={t.test} className="border-b border-white/5 text-sm">
                        <td className="py-3.5 pr-4 text-white">{t.test}</td>
                        <td className="py-3.5 pr-4 font-mono text-xs text-slate-400">{t.specification}</td>
                        <td className="py-3.5 pr-4 font-mono text-xs text-white">{t.result}</td>
                        <td className="py-3.5 text-right">
                          <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 font-mono text-[10px] text-emerald-400">
                            {t.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border-t border-white/10 pt-8">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">TESTING LABORATORY</div>
                  <div className="mt-1 text-sm text-slate-300">{coa.laboratory}</div>
                  <div className="mt-4 font-mono text-[9px] tracking-[0.25em] text-slate-500">STORAGE</div>
                  <div className="mt-1 text-sm text-slate-300">{coa.storage}</div>
                </div>
                <div className="sm:text-right">
                  <div className="font-mono text-[9px] tracking-[0.25em] text-slate-500">APPROVED BY</div>
                  <div className="mt-1 font-display text-lg font-semibold text-white" style={{ fontStyle: "italic" }}>{coa.analyst}</div>
                  <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-[#E8E9EB]/30 bg-[#E8E9EB]/5 px-4 py-2">
                    <ShieldCheck className="h-4 w-4 text-[#E8E9EB]" />
                    <span className="font-mono text-xs tracking-[0.2em] text-[#E8E9EB]" data-testid="coa-verify-code">{coa.verify_code}</span>
                  </div>
                </div>
              </div>
              <p className="mt-8 border-t border-white/5 pt-6 font-mono text-[10px] leading-relaxed tracking-[0.1em] text-slate-600">
                THIS CERTIFICATE VERIFIES THE AUTHENTICITY AND PURITY OF THE LISTED BATCH. FOR LABORATORY RESEARCH USE ONLY.
                NOT FOR HUMAN CONSUMPTION. VERIFY THIS CERTIFICATE AT ALPHA PEPS USING THE CODE ABOVE.
              </p>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
